import React from "react";

import { Artefact } from "@/types/artefacts";
import { ARTEFACT_CONFIG } from "@/config/artefacts";
import { logger } from "@/telemetry";

interface HTMLArtefactViewerProps {
  artefact: Artefact;
}

const createHtmlContent = (content: string, title: string): string => {
  // Check if it's already a complete HTML document
  if (content.includes("<html")) {
    // Inject dark mode styles into existing HTML
    return content.replace(
      "</head>",
      `<style>
        @media (prefers-color-scheme: dark) {
          body {
            background-color: transparent !important;
            color: #e4e4e7 !important;
          }
          table {
            background-color: transparent !important;
          }
          th {
            background-color: #27272a !important;
            color: #e4e4e7 !important;
          }
          td {
            background-color: #18181b !important;
            color: #e4e4e7 !important;
          }
          td, th {
            border-color: #3f3f46 !important;
          }
        }
      </style>
      <script>
        try {
          if (window.parent && window.parent.document) {
            const isDark = window.parent.document.documentElement.classList.contains('dark');
            if (isDark) {
              document.documentElement.classList.add('dark');
              const style = document.createElement('style');
              style.textContent = \`
                body { background-color: transparent !important; color: #e4e4e7 !important; }
                table { background-color: transparent !important; }
                th { background-color: #27272a !important; color: #e4e4e7 !important; }
                td { background-color: #18181b !important; color: #e4e4e7 !important; }
                td, th { border-color: #3f3f46 !important; }
              \`;
              document.head.appendChild(style);
            }
          }
        } catch (e) {}
      </script>
      </head>`,
    );
  }

  return `
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${title}</title>
        <style>
          body {
            margin: 0;
            padding: 20px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: transparent;
            color: #1a1a1a;
          }
          table {
            border-collapse: collapse;
            width: 100%;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
          }
          th {
            background-color: #f2f2f2;
          }
          
          /* Dark mode styles */
          @media (prefers-color-scheme: dark) {
            body {
              color: #e4e4e7;
            }
            table {
              background-color: transparent;
            }
            th {
              background-color: #27272a !important;
              color: #e4e4e7;
            }
            td {
              background-color: #18181b !important;
              color: #e4e4e7;
            }
            th, td {
              border-color: #3f3f46;
            }
          }
          
          /* Check if parent has dark class */
          .dark body {
            color: #e4e4e7;
          }
          .dark table {
            background-color: transparent;
          }
          .dark th {
            background-color: #27272a !important;
            color: #e4e4e7;
          }
          .dark td {
            background-color: #18181b !important;
            color: #e4e4e7;
          }
          .dark th,
          .dark td {
            border-color: #3f3f46;
          }
        </style>
        <script>
          // Detect parent window dark mode
          try {
            if (window.parent && window.parent.document) {
              const isDark = window.parent.document.documentElement.classList.contains('dark');
              if (isDark) {
                document.documentElement.classList.add('dark');
              }
            }
          } catch (e) {
            // Cross-origin restriction, ignore
          }
        </script>
      </head>
      <body>
        ${content}
      </body>
    </html>
  `;
};

const isChartContent = (content: string, title: string): boolean => {
  return (
    content.includes("canvas") ||
    content.includes("Chart") ||
    content.includes("chart") ||
    content.includes("ctx.") ||
    title.toLowerCase().includes("chart")
  );
};

const handleIframeLoad = (e: React.SyntheticEvent<HTMLIFrameElement>) => {
  const iframe = e.target as HTMLIFrameElement;

  if (iframe.contentWindow) {
    try {
      const height = iframe.contentWindow.document.documentElement.scrollHeight;

      iframe.style.height =
        Math.max(
          ARTEFACT_CONFIG.iframe.defaultHeight,
          Math.min(ARTEFACT_CONFIG.iframe.maxHeight, height),
        ) + "px";
    } catch (error) {
      // Only log in non-test environments to reduce test noise
      if (process.env.NODE_ENV !== "test") {
        logger.info("HTML iframe height adjustment failed", { error: String(error) });
      }
    }
  }
};

export const HTMLArtefactViewer: React.FC<HTMLArtefactViewerProps> = ({ artefact }) => {
  const htmlContent = createHtmlContent(artefact.content, artefact.title);
  const isChart = isChartContent(artefact.content, artefact.title);

  return (
    <div className="bg-default-100 dark:bg-default-100/50 rounded-lg border border-default-200 dark:border-default-700">
      <div className="text-xs text-default-600 dark:text-default-400 p-2 font-medium border-b border-default-200 dark:border-default-700">
        {isChart ? "Interactive Chart" : "HTML Content"} • {artefact.metadata?.estimatedSize}
      </div>
      <div className="bg-white dark:bg-default-50">
        <iframe
          key={artefact.id} // Force refresh when artefact changes
          className="w-full border-0"
          data-artefact-id={artefact.id}
          sandbox="allow-scripts allow-same-origin"
          srcDoc={htmlContent}
          style={{ minHeight: `${ARTEFACT_CONFIG.iframe.defaultHeight}px` }}
          onLoad={handleIframeLoad}
        />
      </div>
    </div>
  );
};
